import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { CommonService } from '../../common.service';

export const TutorGuard: CanActivateFn = (route, state) => {
  const commonService = inject(CommonService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const userData = commonService.getProfileDataFromLocalStorage();

  if (!userData) {
    router.navigate(['/login']);
    return false;
  }

  const profileStatus = userData?.profile_status; // 'pending', 'submitted', 'approved', 'rejected'

  if (profileStatus === 'approved') {
    if (state.url.startsWith('/tutor-signup') || state.url === '/thank-you') {
      router.navigate(['/tutor/dashboard']);
      return false;
    }
    return true;
  }

  if (profileStatus === 'submitted') {
    if (state.url === '/thank-you') {
      return true;
    }
    router.navigate(['/thank-you']);
    return false;
  }

  if (profileStatus === 'pending') {
    // Fetch profile status to find the first pending step
    return commonService
      .getProfileStatus()
      .toPromise()
      .then((result: any) => {
        if (result && result.success && result.data) {
          const steps = result.data;
          const firstIncompleteStep = steps.find(
            (step: any) =>
              step.status !== 'completed' &&
              step.status !== 'approved' &&
              step.status !== 'skipped',
          );

          if (firstIncompleteStep) {
            const stepRoutes: { [key: string]: string } = {
              about: '/tutor-signup/about-tutor',
              subjects: '/tutor-signup/subjects',
              languages: '/tutor-signup/languages',
              education: '/tutor-signup/education',
              experience: '/tutor-signup/experience',
              availability: '/tutor-signup/availability',
              rate: '/tutor-signup/rate-per-hour',
              submit: '/tutor-signup/submit',
            };

            const targetRoute =
              stepRoutes[firstIncompleteStep.step_key] ||
              '/tutor-signup/about-tutor';

            // If we are already on a tutor-signup route, check if we are allowed to be here
            if (
              state.url === '/tutor-signup' ||
              state.url === '/tutor-signup/'
            ) {
              router.navigate([targetRoute]);
              return false;
            }

            if (state.url.startsWith('/tutor-signup')) {
              const currentStepKey = Object.keys(stepRoutes).find((key) =>
                state.url.includes(stepRoutes[key]),
              );
              if (currentStepKey) {
                const currentStepObj = steps.find(
                  (s: any) => s.step_key === currentStepKey,
                );
                const firstIncompleteOrder = firstIncompleteStep.step_order;
                const currentStepOrder = currentStepObj
                  ? currentStepObj.step_order
                  : 0;

                // If trying to go beyond progress, redirect to first incomplete
                if (currentStepOrder > firstIncompleteOrder) {
                  router.navigate([targetRoute]);
                  return false;
                }
                // Allow if same step or previous step (for editing/Back button)
                return true;
              }
              // If on a sub-route not recognized, redirect to first incomplete
              router.navigate([targetRoute]);
              return false;
            }

            // If not on signup route, redirect to first incomplete
            router.navigate([targetRoute]);
          } else {
            if (state.url.startsWith('/tutor-signup')) {
              return true;
            }
            router.navigate(['/tutor-signup/submit']);
          }
        } else {
          if (state.url.startsWith('/tutor-signup')) {
            return true;
          }
          router.navigate(['/tutor-signup/about-tutor']);
        }
        return false;
      })
      .catch(() => {
        if (state.url.startsWith('/tutor-signup')) {
          return true;
        }
        router.navigate(['/tutor-signup/about-tutor']);
        return false;
      });
  }

  if (profileStatus === 'rejected') {
    // For now, redirect to thank-you if rejected, or handle specifically if needed
    if (state.url === '/thank-you') {
      return true;
    }
    router.navigate(['/thank-you']);
    return false;
  }

  router.navigate(['/login']);
  return false;
};
